"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useOneTrust = void 0;
var _react = require("react");
const cookiePolicyId = 'optanon-cookie-policy';
const onetrustScriptId = 'onetrust_js';
const exemptLocales = ['ko-KR', 'ja-JP'];

/**
 * OneTrust react hook to add the OneTrust script to the app.
 *
 * @param {string} dataDomainScript The unique OneTrust data domain script string for the domain.
 * @param {string} locale The locale of the app.
 * @param {string} view The current page/view of the app.
 * @param {string} webview Whether the app is in a webview or not.
 *
 */
const useOneTrust = _ref => {
  let {
    dataDomainScript,
    locale,
    view,
    webview
  } = _ref;
  /**
   * Adds the OneTrust script to the head of the document.
   */
  const addOneTrustScript = () => {
    const isValidLocale = !exemptLocales.includes(locale);
    const isJapaneseAndLoginPage = locale === 'ja-JP' && view === '/login';
    if (!document.getElementById(onetrustScriptId) && (isValidLocale || isJapaneseAndLoginPage) && dataDomainScript) {
      window.OptanonWrapper = () => {};

      // Add cookie policy div to app for OneTrust to populate.
      if (!document.getElementById(cookiePolicyId)) {
        const cookiePolicyHolder = document.createElement('div');
        cookiePolicyHolder.setAttribute('id', cookiePolicyId);
        cookiePolicyHolder.style.display = 'none';
        document.body.appendChild(cookiePolicyHolder);
      }
      const oneTrustScript = document.createElement('script');
      oneTrustScript.setAttribute('id', 'onetrust_js');
      oneTrustScript.setAttribute('src', 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js');
      oneTrustScript.setAttribute('type', 'text/javascript');
      oneTrustScript.setAttribute('data-document-language', 'true');
      oneTrustScript.setAttribute('data-domain-script', dataDomainScript);
      document.head.appendChild(oneTrustScript);
    }
  };

  // Verify that the modal content was hydrated after calling `hydrateCookiePolicyModal`.
  const modalContentHydrated = () => {
    const cookiePolicyHolder = document.getElementById('optanon-cookie-policy-holder');
    const cookiePolicyTitle = document.getElementById('cookie-policy-title');
    return cookiePolicyHolder === null || cookiePolicyHolder === void 0 ? void 0 : cookiePolicyHolder.contains(cookiePolicyTitle);
  };

  /**
   * Opens the cookie policy modal for non ko-KR, ja-JP, and en-US locales.
   *
   * Requires 2 divs with ids:
   * - optanon-cookie-policy-holder
   * - optanon-cookie-policy
   *
   * The #optanon-cookie-policy is inserted in the `addOneTrustScript` method.
   *
   * Since we can't be sure how long it will take for the cookie policy to appear on the page, we should retry
   * this method until we've verified that the cookie policy was copied and appended.
   */
  const hydrateCookiePolicyModal = () => {
    try {
      const clonedCookiePolicy = document.getElementById(cookiePolicyId).cloneNode(true);
      document.getElementById('optanon-cookie-policy-holder').appendChild(clonedCookiePolicy);
      clonedCookiePolicy.style.display = 'block';
    }
    // We ignore errors and retry until we verify success with `modalContentHydrated`.
    catch (error) {} // eslint-disable-line no-empty
  };

  /**
   * Removes the OneTrust script and the cookie policy holder from the document.
   */
  const removeOneTrustScript = () => {
    var _document$getElementB, _document$getElementB2, _document$getElementB3;
    (_document$getElementB = document.getElementById(onetrustScriptId)) === null || _document$getElementB === void 0 ? void 0 : _document$getElementB.remove();
    (_document$getElementB2 = document.getElementById(cookiePolicyId)) === null || _document$getElementB2 === void 0 ? void 0 : _document$getElementB2.remove();
    (_document$getElementB3 = document.getElementById('onetrust-consent-sdk')) === null || _document$getElementB3 === void 0 ? void 0 : _document$getElementB3.remove();
  };
  (0, _react.useEffect)(() => {
    if (webview) {
      removeOneTrustScript();
    } else {
      addOneTrustScript();
    }
  }, [dataDomainScript, locale, view, webview]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    hydrateCookiePolicyModal,
    modalContentHydrated
  };
};
exports.useOneTrust = useOneTrust;